'use client';

/**
 * TestCasesPanel - Test cases list for copilot
 *
 * Shows test cases contextually:
 * - analysis_run: all test cases from registry
 * - package: test cases targeting that package
 *
 * Each row has a play button and inline result display.
 *
 * @layer presentation
 * @arch_component_type panel
 * @arch_domain testing
 */

import { useState, useEffect } from 'react';
import {
  FlaskConical,
  Play,
  Loader2,
  CheckCircle2,
  XCircle,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils/cn';
import { api } from '@/lib/api/client';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TestCase {
  case_id: string;
  name: string;
  description: string;
  category: string;
  user_outcome: string;
  business_value: string;
  apis_tested: string[];
  targets?: string[];
}

interface RunResult {
  case_id: string;
  name: string;
  success: boolean;
  duration_ms: number;
  summary: string;
  run_semantic_id?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface TestCasesPanelProps {
  entityType: string | null;
  entityId: string | null;
}

export function TestCasesPanel({ entityType, entityId }: TestCasesPanelProps) {
  const [cases, setCases] = useState<TestCase[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const selectNode = useGraphSelectionStore((s) => s.selectNode);

  useEffect(() => {
    const fetchCases = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await api.get<TestCase[]>('/testing/cases');
        setCases(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load test cases');
      } finally {
        setIsLoading(false);
      }
    };
    fetchCases();
  }, []);

  // Filter based on entity context
  const contextCases = cases?.filter((c) => {
    // If viewing a package, filter to tests targeting it
    if (entityType === 'package' && entityId) {
      const packageName = entityId.toLowerCase();
      return c.targets?.some((t) => t.toLowerCase().includes(packageName)) ?? false;
    }
    // analysis_run or other: show all
    return true;
  });

  // Apply text filter
  const filteredCases = contextCases?.filter((c) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      c.case_id.toLowerCase().includes(q) ||
      c.category.toLowerCase().includes(q)
    );
  });

  // Group by category
  const grouped = filteredCases?.reduce<Record<string, TestCase[]>>((acc, c) => {
    if (!acc[c.category]) acc[c.category] = [];
    acc[c.category].push(c);
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading test cases...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-destructive">{error}</div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Filter */}
      <div className="p-3 border-b border-border">
        <Input
          placeholder="Filter tests..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="h-7 text-xs"
        />
        {entityType === 'package' && (
          <p className="text-[10px] text-muted-foreground mt-1">
            Showing tests targeting this package
          </p>
        )}
      </div>

      {/* Cases */}
      <div className="flex-1 overflow-auto">
        {!grouped || Object.keys(grouped).length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-muted-foreground">
            <FlaskConical className="w-8 h-8 mb-2 opacity-30" />
            <p className="text-sm">
              {filter
                ? 'No matching test cases'
                : entityType === 'package'
                ? 'No tests target this package'
                : 'No test cases found'}
            </p>
          </div>
        ) : (
          <div className="py-1">
            {Object.entries(grouped)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([category, categoryCases]) => (
                <CategoryGroup key={category} category={category} cases={categoryCases} />
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Category Group
// ---------------------------------------------------------------------------

function CategoryGroup({ category, cases }: { category: string; cases: TestCase[] }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div>
      <div
        className="flex items-center gap-1 px-3 py-1.5 text-xs cursor-pointer hover:bg-accent/50"
        onClick={() => setExpanded(!expanded)}
      >
        <ChevronRight className={cn('w-3 h-3 transition-transform', expanded && 'rotate-90')} />
        <span className="font-semibold text-muted-foreground uppercase">{category}</span>
        <Badge variant="secondary" className="text-[9px] px-1 ml-auto">{cases.length}</Badge>
      </div>
      {expanded && (
        <div className="ml-3 border-l border-border/50">
          {cases.map((c) => (
            <TestCaseRow key={c.case_id} testCase={c} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Test Case Row
// ---------------------------------------------------------------------------

function TestCaseRow({ testCase }: { testCase: TestCase }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RunResult | null>(null);

  const selectNode = useGraphSelectionStore((s) => s.selectNode);

  const handleRun = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setRunning(true);
    setResult(null);
    try {
      const res = await api.post<RunResult>(`/testing/run/${testCase.case_id}`);
      setResult(res);
      if (res.run_semantic_id) {
        selectNode({
          id: res.run_semantic_id,
          type: 'test_run',
          label: `${testCase.name} (${res.success ? 'Pass' : 'Fail'})`,
        });
      }
    } catch {
      setResult({
        case_id: testCase.case_id,
        name: testCase.name,
        success: false,
        duration_ms: 0,
        summary: 'Failed to run test',
      });
    } finally {
      setRunning(false);
    }
  };

  const handleSelect = () => {
    selectNode({
      id: testCase.case_id,
      type: 'test_case',
      label: testCase.name,
      properties: testCase as unknown as Record<string, unknown>,
    });
  };

  return (
    <div>
      <div
        className="flex items-center gap-1.5 px-2 py-1.5 text-xs cursor-pointer hover:bg-accent/50"
        onClick={handleSelect}
      >
        {/* Status indicator */}
        <div className="w-3.5 h-3.5 shrink-0 flex items-center justify-center">
          {result ? (
            result.success ? (
              <CheckCircle2 className="w-3 h-3 text-green-500" />
            ) : (
              <XCircle className="w-3 h-3 text-red-500" />
            )
          ) : (
            <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/30" />
          )}
        </div>

        {/* Name */}
        <span className="truncate flex-1 text-sm">{testCase.name}</span>

        {/* Duration */}
        {result && (
          <span className="text-[9px] text-muted-foreground">
            {Math.round(result.duration_ms)}ms
          </span>
        )}

        {/* Play button */}
        <Button
          size="sm"
          variant="ghost"
          className="h-5 w-5 p-0"
          onClick={handleRun}
          disabled={running}
        >
          {running ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Play className="w-3 h-3" />
          )}
        </Button>
      </div>

      {/* Inline result */}
      {result && (
        <div
          className={cn(
            'mx-2 mb-1 px-2 py-1 rounded text-[10px]',
            result.success
              ? 'bg-green-500/10 text-green-700 dark:text-green-400'
              : 'bg-red-500/10 text-red-700 dark:text-red-400'
          )}
        >
          {result.summary}
        </div>
      )}
    </div>
  );
}
